import { ACTIVITIES_URI } from "config/route-path"
import GroupsIcon from "icons/Groups"

import { colors } from "@bullseye/tailwind-config"

import { CheckIcon, HeartIcon, Shield, SmartphoneIcon } from "../icons"
import CalendarIcon from "../icons/CalendarIcon"

const howItWorks = {
  ctaText: "Join us",
  heading: "How Hank Works",
  subheading: `We connect you with like-minded people and things to do in your community.`,
  steps: [
    {
      title: "Become a Member",
      description: `Hank is completely free for inaugural members: All you need to do is create an account using your email address!`,
      img: {
        url: "https://storage.googleapis.com/hank-app-assets/marketing/membership-with-sparkes.png",
      },
      ctaText: "Become a member",
      path: "/signup",
    },
    {
      title: "Join an activity (or a few)",
      description: `Follow your passions to find a nearby activity that piques your interest. From sports, to fashion, to canasta, you're sure to find something you like.`,
      img: {
        url: "https://storage.googleapis.com/hank-app-assets/marketing/group-with-sparkles.png",
      },
      ctaText: "Browse activities",
      path: ACTIVITIES_URI,
    },
    {
      title: "Say hello",
      description: `New friendships start with a simple hello. After joining an activity, introduce yourself to other members and get to chatting with a comment or a direct message.`,
      img: {
        url: "https://storage.googleapis.com/hank-app-assets/marketing/add-user-with-sparkles.png",
      },
      ctaText: "Browse activities",
      path: ACTIVITIES_URI,
    },
    {
      title: "Make more plans",
      description: `Hank is a community built for you, and by you. Make the most of it by proposing an activity of your own. Want to take a walk? Have an extra ticket to a game? Go ahead! If you post it, they will come.`,
      img: {
        url: "https://storage.googleapis.com/hank-app-assets/marketing/add-user-with-sparkles.png",
      },
      ctaText: "Browse activities",
      path: ACTIVITIES_URI,
    },
  ],
}

const safety = {
  heading: "Ready to get started? Here's how Hank works:",
  reverse: true,
  ctaText: "Become a member for free",
  checklist: [
    {
      subheading: "An introduction to tens of thousands of other Hank members",
      Icon: () => (
        <GroupsIcon
          outline={false}
          scale={1}
          lineScale={1}
          color={colors.primary[700]}
          style={{ margin: "-0.75rem" }}
        />
      ),
    },
    {
      subheading: `Peace of mind knowing that every other Hank member is verified`,
      Icon: () => (
        <Shield scale={1} strokeWidth={2} color={colors.primary[700]} />
      ),
    },
    {
      subheading: `Online tools that take the guesswork out of new friendships and scheduling`,
      Icon: () => <SmartphoneIcon color={colors.primary[700]} />,
    },
    {
      subheading: `Access to thousands of in-person activities hosted by other Hank members`,
      Icon: () => (
        <CalendarIcon
          scale={1}
          lineScale={1}
          color={colors.primary[700]}
          outline={false}
          style={{ margin: "-0.75rem" }}
        />
      ),
    },
    {
      subheading: `Full time Hank staff that’s here to lend a hand around the clock`,
      Icon: () => (
        <HeartIcon scale={1.5} lineScale={0.75} color={colors.primary[700]} />
      ),
    },
  ],
  checkout_cheklist: [
    {
      subheading: `Anytime cancellation: If you're not happier with Hank, we'll cancel, no questions asked.`,
      Icon: () => (
        <CheckIcon scale={1} strokeWidth={2} color={colors.primary[700]} />
      ),
    },
    {
      subheading: `Worry-free friendships: Keeping Hank a paid community helps ensure the safety of all members.`,
      Icon: () => (
        <CheckIcon scale={1} strokeWidth={2} color={colors.primary[700]} />
      ),
    },
    {
      subheading: `Maximum convenience: Annual billing means payment plans don't get in the way of your connections.`,
      Icon: () => (
        <CheckIcon scale={1} strokeWidth={2} color={colors.primary[700]} />
      ),
    },
  ],
}

const pageNotFound = {
  heading: "This page isn't available",
  subheading: {
    line1: "The link may be broken, or the page may have been removed.",
    cta: {
      href: ACTIVITIES_URI,
      text: "View activities near you",
    },
    linkHome: {
      text: "Home",
      to: "/",
    },
    linkMailTo: {
      text: "Visit Help Center",
      href: "https://help.gethank.com",
    },
  },
}

const errorFallback = {
  heading: "Oops! An error occurred",
}

const blogPreview = {
  heading: "Hank Happenings",
  subheading: "Stories and articles from around the Hank community",
  posts: [
    {
      title:
        "Hank Joins the Wider Circle Family to Expand Offerings for Adults 55+",
      url: "/blog-post/hank-joins-the-wider-circle-family-to-expand-opportunities-for-adults-55",
      image_url:
        "https://storage.googleapis.com/hank-app-assets/marketing/wider-circle-blog-image.png",
      description:
        "Hank will serve as an extension of Wider Circle’s core offerings, broadening our reach to help adults 55+ come together for fun and engaging activities, regardless of health plan affiliation.",
    },
    {
      title: "Becoming a Hank Ambassador is Fun, Easy, and Rewarding!",
      url: "/blog-post/becoming-a-hank-ambassador-is-fun-easy-and-rewarding",
      image_url:
        "https://storage.googleapis.com/hank-app-assets/marketing/ambassador-blog-image.jpg",
      description:
        "Are you looking to meet new friends, bring people together, and have a great time? Then becoming a Hank Ambassador might be the perfect role for you!",
    },
    {
      title: "Partner with Hank",
      url: "/blog-post/partner-with-hank",
      image_url:
        "https://cdn.prod.website-files.com/61e0ec12432a602eecf596ac/68dc21c48e07e82630a07636_Existing%20Partners%20(7).png",
      description:
        "The Hank Community Partner Program is a collaborative initiative that invites organizations to share their events and activities with a wider audience.",
    },
  ],
}

const copy = {
  home: {
    textBlock: {
      line1: "Wherever you want to belong, Hank can help.",
      line2:
        "The Hank Community is made up of active older adults looking to meet people, learn new things, pursue old passions—and everything in between.",
    },
    subhero: {
      text: "Hank is an AARP Innovation Labs Portfolio Company",
      imgSrc: "/images/aarp.png",
    },
    howItWorks,
    blogPreview,
    safety,
  },
  pageNotFound,
  errorFallback,
}

export default copy
